import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../styles/components/ResultPage.css";
import omnicareLogo from "../assets/icons/logo.svg";
import doctorImage from "../assets/images/doctor.png";
import backIcon from "../assets/icons/back.svg";
import LanguageSwitcher from "./LanguageSwitcher";
import { ACADEMIC_REFERENCES } from "../utils/cancerRecommendations";
import { generateHealthReport, savePdfMobileFriendly, isMobileDevice } from "../utils/generateReport";
import { saveEmail } from "../utils/emailCollection";
import FEATURE_FLAGS from "../config/featureFlags";

const ResultPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expandedCards, setExpandedCards] = useState({});
  const [reportEmail, setReportEmail] = useState("");
  const [secondaryExpanded, setSecondaryExpanded] = useState(false);
  const [reportStatus, setReportStatus] = useState(""); // "" | "sending" | "downloaded" | "emailed"
  const reportEmailRef = useRef(null);

  const handleDownloadReport = async () => {
    const results = getAnalysisResults();
    if (!results) return;
    const doc = generateHealthReport(results, t("common.lang") || "en");
    if (isMobileDevice()) {
      await savePdfMobileFriendly(doc, "Pebble Health-Health-Report.pdf");
    } else {
      doc.save("Pebble Health-Health-Report.pdf");
    }
    setReportStatus("downloaded");
  };

  const handleShareReport = async () => {
    const results = getAnalysisResults();
    if (!results) return;
    const doc = generateHealthReport(results, t("common.lang") || "en");
    const result = await savePdfMobileFriendly(doc, "Pebble Health-Health-Report.pdf", { preferShare: true });
    if (result === "shared") {
      setReportStatus("downloaded");
    }
  };

  const handleEmailReport = async (e) => {
    e.preventDefault();
    if (!reportEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reportEmail)) {
      if (reportEmailRef.current) reportEmailRef.current.focus();
      return;
    }
    setReportStatus("sending");
    try {
      await saveEmail(reportEmail, {
        locale: t("common.lang") || "en",
        source: "report",
      });
      // Download the PDF for the user
      const results = getAnalysisResults();
      if (results) {
        const doc = generateHealthReport(results, t("common.lang") || "en");
        if (isMobileDevice()) {
          await savePdfMobileFriendly(doc, "Pebble Health-Health-Report.pdf");
        } else {
          doc.save("Pebble Health-Health-Report.pdf");
        }
      }
      setReportStatus("emailed");
    } catch {
      setReportStatus("downloaded");
    }
  };

  const handleBackToHome = () => navigate("/");
  const handleLearnMore = () => navigate("/programs");
  const handlePrimaryAction = () => navigate("/after-quiz/1");

  const toggleCardExpand = (cardId) => {
    setExpandedCards((prev) => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const getAnalysisResults = () => {
    try {
      const stored = localStorage.getItem("omnicare_analysis_results");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const analysisResults = getAnalysisResults();
  const { surveyData, recommendations, healthManual, healthScreening = [] } = analysisResults || {};
  const { stronglyRecommend = [], recommend = [], belowAverage = [] } = recommendations || {};
  const [showBelowAverage, setShowBelowAverage] = useState(false);
  const {
    diet = [],
    lifestyle = [],
    vitamins = [],
    exercise = [],
    prevention = [],
    screening: manualScreening = [],
  } = healthManual || {};

  // Use generic greeting instead of hardcoded names
  const userName = surveyData?.name || "";

  const RiskMeter = ({ score, level, color }) => {
    // Normalize score to 0-100 for the meter (cap at 10x)
    const normalized = Math.min((score / 10) * 100, 100);
    return (
      <div className="risk-meter">
        <div className="risk-meter-bar">
          <div
            className="risk-meter-fill"
            style={{
              width: `${normalized}%`,
              backgroundColor: color,
            }}
          />
          <div className="risk-meter-labels">
            <span>LOW</span>
            <span>MODERATE</span>
            <span>ELEVATED</span>
            <span>HIGH</span>
          </div>
        </div>
        <div className="risk-meter-score">
          {score.toFixed(1)}x {t("results.baselineRisk")}
        </div>
      </div>
    );
  };

  const generateScreeningCards = () => {
    const cards = [];

    const mapItem = (item, index, type) => ({
      id: `${type}-${index + 1}`,
      title: `${item.cancer} ${t("results.screening")}`,
      riskLevel: item.riskLevel,
      riskColor: item.riskColor,
      cssClass: item.cssClass || "low-risk",
      riskScore: item.riskScore || 1.0,
      description: item.description,
      riskFactors: item.riskFactors || [],
      statistics: item.statistics || [],
      screening: item.screening || null,
      primaryAction:
        type === "strong"
          ? t("common.scheduleScreening")
          : t("common.considerScreening"),
      secondaryAction: t("common.learnMore"),
      type,
      tier: item.tier || "secondary",
      guidelineSource: item.guidelineSource || null,
      guidelineNote: item.guidelineNote || null,
    });

    stronglyRecommend.forEach((item, i) =>
      cards.push(mapItem(item, i, "strong"))
    );
    recommend.forEach((item, i) => cards.push(mapItem(item, i, "rec")));
    belowAverage.forEach((item, i) => cards.push(mapItem(item, i, "below-avg")));

    if (cards.length === 0) {
      cards.push({
        id: "default",
        title: t("results.generalHealth"),
        riskLevel: t("results.lowRisk"),
        riskColor: "#28a745",
        cssClass: "low-risk",
        riskScore: 1.0,
        description: t("results.generalDescription"),
        riskFactors: [],
        statistics: [],
        screening: null,
        primaryAction: t("common.scheduleCheckup"),
        secondaryAction: t("common.learnMore"),
        type: "general",
      });
    }

    return cards;
  };

  const screeningCards = generateScreeningCards();

  // Split cards into primary (government-guideline-backed) and secondary (personalized)
  const primaryCards = FEATURE_FLAGS.PRIMARY_SECONDARY_DASHBOARD
    ? screeningCards.filter((c) => c.tier === "primary")
    : screeningCards;
  const secondaryCardsAll = FEATURE_FLAGS.PRIMARY_SECONDARY_DASHBOARD
    ? screeningCards.filter((c) => c.tier !== "primary")
    : [];
  const secondaryCards = secondaryCardsAll.filter((c) => c.type !== "below-avg");
  const belowAvgCards = secondaryCardsAll.filter((c) => c.type === "below-avg");

  const generateHealthManualSections = () => {
    const sections = [];
    const sectionDefs = [
      { key: "diet", icon: "🥬", title: t("results.sections.nutritionWeight"), items: diet },
      { key: "lifestyle", icon: "🚭", title: t("results.sections.lifestyleFactors"), items: lifestyle },
      { key: "exercise", icon: "🏃", title: t("results.sections.physicalActivity"), items: exercise },
      { key: "screening", icon: "🔬", title: t("results.sections.screeningTimeline"), items: manualScreening },
      { key: "prevention", icon: "🛡️", title: t("results.sections.preventionMonitoring"), items: prevention },
      { key: "vitamins", icon: "💊", title: t("results.sections.supplementsNutrients"), items: vitamins },
    ];
    for (const def of sectionDefs) {
      if (def.items && def.items.length > 0) {
        sections.push(def);
      }
    }
    return sections;
  };

  const healthManualSections = generateHealthManualSections();

  // Check if health manual items are structured (v2) or plain strings (v1)
  const isStructuredManual = healthManualSections.length > 0 &&
    healthManualSections[0].items.length > 0 &&
    typeof healthManualSections[0].items[0] === "object";

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "high": return "priority-high";
      case "medium": return "priority-medium";
      default: return "priority-low";
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "high": return t("results.priorityLabels.priority");
      case "medium": return t("results.priorityLabels.recommended");
      default: return t("results.priorityLabels.maintenance");
    }
  };

  // Translate health tip text via i18n keys
  const translateHealthTip = (tipText) => {
    const tipMapping = {
      "Ensure adequate caloric intake with nutrient-rich foods":
        "results.healthTips.ensure_adequate_caloric_intake",
      "Reduce sodium intake and processed foods":
        "results.healthTips.reduce_sodium_intake",
      "Limit saturated fats and focus on healthy fats (avocado, olive oil, nuts)":
        "results.healthTips.limit_saturated_fats",
      "Increase fiber intake through whole grains and vegetables":
        "results.healthTips.increase_fiber_intake",
      "Maintain a healthy weight through balanced diet and exercise":
        "results.healthTips.maintain_healthy_weight",
      "Stay hydrated with water and limit sugary beverages":
        "results.healthTips.stay_hydrated",
      "Get regular exercise - aim for at least 150 minutes per week":
        "results.healthTips.get_regular_exercise",
      "Avoid smoking and secondhand smoke exposure":
        "results.healthTips.avoid_smoking",
      "Limit alcohol consumption to moderate levels":
        "results.healthTips.limit_alcohol",
      "Get adequate sleep (7-9 hours per night)":
        "results.healthTips.get_adequate_sleep",
      "Manage stress through relaxation techniques and hobbies":
        "results.healthTips.manage_stress",
      "Schedule regular health checkups and screenings":
        "results.healthTips.regular_checkups",
      "Protect skin from sun exposure with SPF and protective clothing":
        "results.healthTips.sun_protection",
      "Stay up to date with recommended vaccinations":
        "results.healthTips.vaccinations",
      "Consider vitamin D supplementation if deficient":
        "results.healthTips.vitamin_d",
      "Include omega-3 fatty acids in your diet (fish, flaxseeds)":
        "results.healthTips.omega_3",
      "Eat foods rich in antioxidants (berries, leafy greens, nuts)":
        "results.healthTips.antioxidants",
      "Include probiotic foods for gut health (yogurt, kefir, sauerkraut)":
        "results.healthTips.probiotics",
      "Maintain regular sleep schedule (7-9 hours per night)":
        "results.healthTips.maintain_regular_sleep",
      "Practice stress management techniques":
        "results.healthTips.practice_stress_management",
      "Iron (if not getting enough from diet)":
        "results.healthTips.iron_supplement",
      "Folic acid for reproductive health": "results.healthTips.folic_acid",
      "Include strength training to build healthy muscle mass":
        "results.healthTips.strength_training",
      "Multivitamin for general health support":
        "results.healthTips.multivitamin",
      "Omega-3 fatty acids for heart health":
        "results.healthTips.omega_3_heart",
      "Aim for 150 minutes of moderate exercise per week":
        "results.healthTips.moderate_exercise",
      "Include both cardio and strength training":
        "results.healthTips.cardio_and_strength",
      "Focus on portion control and balanced meals":
        "results.healthTips.portion_control",
      "Maintain your healthy eating habits":
        "results.healthTips.maintain_healthy_eating",
      "Consider adding more colorful fruits and vegetables":
        "results.healthTips.colorful_fruits_vegetables",
      "Consider smoking cessation programs or nicotine replacement therapy":
        "results.healthTips.smoking_cessation",
      "Consider smoking cessation programs or nicotine replacement therapy — quitting reduces cancer risk within 5-10 years":
        "results.healthTips.smoking_cessation_v2",
      "Track your smoking triggers and develop coping strategies":
        "results.healthTips.track_smoking_triggers",
      "Keep an alcohol journal to track drinks per week":
        "results.healthTips.alcohol_journal",
      "Consider alcohol-free days each week":
        "results.healthTips.alcohol_free_days",
      "Vitamin D for bone strength and immune support":
        "results.healthTips.vitamin_d_bone",
      "Calcium for bone health": "results.healthTips.calcium",
      "Monitor blood sugar levels regularly":
        "results.healthTips.monitor_blood_sugar",
      "Work with your healthcare provider on diabetes management":
        "results.healthTips.diabetes_management",
      "Monitor blood pressure regularly":
        "results.healthTips.monitor_blood_pressure",
      "Consider heart-healthy diet modifications":
        "results.healthTips.heart_healthy_diet",
      "Focus on whole grains and lean proteins":
        "results.healthTips.whole_grains_proteins",
      "Include a variety of colorful fruits and vegetables":
        "results.healthTips.variety_fruits_vegetables",
      "Vitamin D (600-800 IU/day) for bone health and possible cancer risk reduction":
        "results.healthTips.vitamin_d_cancer",
      "Calcium (1,000-1,200mg/day) for bone health":
        "results.healthTips.calcium_dosage",
      "Aim for gradual weight loss of 1-2 lbs/week through caloric deficit — obesity increases risk for 13+ cancer types (NCI)":
        "results.healthTips.weight_loss_cancer",
      "Start with 150 min/week moderate activity, progress to 300 min/week for weight management (ACS)":
        "results.healthTips.exercise_weight_acs",
      "Aim for 150-300 minutes of moderate exercise per week to maintain healthy weight":
        "results.healthTips.exercise_maintain",
      "Reduce sodium intake to <2,300mg/day — high sodium is linked to gastric cancer (WCRF)":
        "results.healthTips.sodium_gastric",
      "Limit processed and red meat to <18oz/week — associated with colorectal cancer risk (IARC)":
        "results.healthTips.red_meat_colorectal",
      "Increase fiber intake to 25-30g/day through whole grains and vegetables":
        "results.healthTips.fiber_specific",
      "Great job quitting smoking! Risk continues to decrease over time — after 10 years, lung cancer risk drops by ~50%":
        "results.healthTips.quit_smoking_congrats",
      "Strongly consider reducing alcohol — >14 drinks/week significantly increases cancer risk for liver, breast, and colon (ACS)":
        "results.healthTips.reduce_alcohol_cancer",
      "Keep alcohol intake at or below 7 drinks/week (women) or 14/week (men) per ACS guidelines":
        "results.healthTips.alcohol_acs_limit",
      "Monitor blood sugar levels regularly — diabetes increases colorectal and liver cancer risk":
        "results.healthTips.blood_sugar_cancer",
      "Schedule regular check-ups with your healthcare provider":
        "results.healthTips.regular_checkups",
    };

    const key = tipMapping[tipText];
    return key ? t(key) : tipText;
  };

  return (
    <div className="result-page-container">
      {/* Header */}
      <header className="result-page-header">
        <div className="logo-section">
          <div
            className="logo"
            onClick={() => navigate("/landing")}
            style={{ cursor: "pointer" }}
          >
            <img src={omnicareLogo} alt="Pebble Health Logo" className="logo-icon" />
            <span className="logo-text">Pebble Health</span>
          </div>
        </div>
        <div className="header-actions">
          <LanguageSwitcher />
          <div className="back-button" onClick={handleBackToHome}>
            <img src={backIcon} alt="Back" className="back-icon" />
          </div>
        </div>
      </header>

      {/* Doctor intro — compact inline avatar */}
      <div className="doctor-intro">
        <img src={doctorImage} alt="Doctor" className="doctor-avatar-sm" />
        <p className="intro-text">
          {userName
            ? t("results.introNamed", { name: userName })
            : t("results.intro")}
        </p>
      </div>

      <p className="result-disclaimer">
        {t("results.disclaimer")}
      </p>

      <div className="result-content">
        {/* Screening Suggestions */}
        <div className="results-screening-section">

          {/* Primary Screenings — Government-Guideline-Backed */}
          {FEATURE_FLAGS.PRIMARY_SECONDARY_DASHBOARD && primaryCards.length === 0 && (
            <div className="primary-screening-dashboard no-guidelines">
              <div className="dashboard-header">
                <h2 className="dashboard-title">🏥 {t("results.primaryScreeningsTitle", "National Guidelines")}</h2>
                <p className="dashboard-subtitle">{t("results.noGuidelinesYet", "No government-recommended screenings at your age yet. Most routine cancer screenings begin between ages 40-50. Here's what's coming up:")}</p>
              </div>
              <div className="upcoming-screenings-timeline">
                <div className="timeline-item">
                  <span className="timeline-age">{t("results.timeline.age40", "Age 40")}</span>
                  <span className="timeline-desc">{t("results.timeline.age40desc", "Breast cancer screening (mammogram) for women")}</span>
                </div>
                <div className="timeline-item">
                  <span className="timeline-age">{t("results.timeline.age45", "Age 45")}</span>
                  <span className="timeline-desc">{t("results.timeline.age45desc", "Colorectal cancer screening for all adults")}</span>
                </div>
                <div className="timeline-item">
                  <span className="timeline-age">{t("results.timeline.age50", "Age 50")}</span>
                  <span className="timeline-desc">{t("results.timeline.age50desc", "Lung cancer screening (if smoking history); prostate discussion (men)")}</span>
                </div>
              </div>
            </div>
          )}

          {FEATURE_FLAGS.PRIMARY_SECONDARY_DASHBOARD && primaryCards.length > 0 && (
            <div className="primary-screening-dashboard">
              <div className="dashboard-header">
                <h2 className="dashboard-title">🏥 {t("results.primaryScreeningsTitle", "National Guidelines")}</h2>
                <p className="dashboard-subtitle">{t("results.primaryScreeningsSubtitle", "You meet the eligibility criteria for these government-recommended screenings based on your age, sex, and risk profile.")}</p>
              </div>
              <div className="dashboard-cards">
                {primaryCards.map((card) => (
                  <div key={card.id} className={`dashboard-card ${card.cssClass}`}>
                    <div className="dashboard-card-top">
                      <h3 className="dashboard-card-title">🩺 {card.title}</h3>
                      <span className="risk-level" style={{ backgroundColor: card.riskColor }}>{card.riskLevel}</span>
                    </div>
                    {card.riskScore > 0 && (
                      <RiskMeter score={card.riskScore} level={card.riskLevel} color={card.riskColor} />
                    )}
                    <p className="card-description">{card.description}</p>
                    {card.screening && (
                      <div className="dashboard-screening-summary">
                        <div className="screening-detail"><strong>{t("results.method")}:</strong> {card.screening.method}</div>
                        <div className="screening-detail"><strong>{t("results.interval")}:</strong> {card.screening.interval}</div>
                        <div className="screening-source">{card.screening.source}</div>
                      </div>
                    )}
                    {card.guidelineSource && (
                      <div className="guideline-source">📖 {card.guidelineSource}</div>
                    )}
                    <span className="primary-badge">{t("results.guidelineBacked", "✓ Guideline-Backed")}</span>
                    <div className="card-actions">
                      <button className="action-button primary" onClick={handlePrimaryAction}>{card.primaryAction}</button>
                      <button className="action-button secondary" onClick={handleLearnMore}>{card.secondaryAction}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Secondary Screenings — Personalized/Research-Based (Always visible, risk factors collapsed) */}
          {FEATURE_FLAGS.PRIMARY_SECONDARY_DASHBOARD && secondaryCards.length > 0 && (
            <div className="secondary-screening-section">
              <div className="secondary-section-header">
                <h2>🔬 {t("results.secondaryScreeningsTitle", "Personalized Suggestions")}</h2>
                <p className="secondary-section-subtitle">
                  {t("results.secondaryScreeningsSubtitle", "Based on your family history, lifestyle & risk factors")}
                </p>
              </div>
              <div className="screening-cards">
                {secondaryCards.map((card) => (
                  <div key={card.id} className={`screening-card ${card.cssClass}`}>
                    <div className="card-header">
                      <div className="card-title"><h2>🩺 {card.title}</h2></div>
                      <div className="risk-level" style={{ backgroundColor: card.riskColor }}>{card.riskLevel}</div>
                    </div>
                    <p className="card-description">{card.description}</p>
                    {card.guidelineNote && (
                      <div className="guideline-note">ℹ️ {card.guidelineNote}</div>
                    )}
                    {card.riskFactors.length > 0 && (
                      <details className="risk-factors-collapsible">
                        <summary className="risk-factors-toggle">
                          {t("results.riskFactors", "Your Risk Factors")} ({card.riskFactors.length}) ▼
                        </summary>
                        <div className="risk-factors">
                          <ul>{card.riskFactors.map((rf, index) => (
                            <li key={index} className="risk-factor-item">
                              <div className="risk-factor-header">
                                <span className="risk-factor-name">{rf.factor || rf}</span>
                                {rf.multiplier && <span className="risk-factor-multiplier">{rf.multiplier.toFixed(1)}x</span>}
                              </div>
                              {rf.detail && <p className="risk-factor-detail">{rf.detail}</p>}
                            </li>
                          ))}</ul>
                        </div>
                      </details>
                    )}
                    {card.riskScore > 0 && <RiskMeter score={card.riskScore} level={card.riskLevel} color={card.riskColor} />}
                    {card.screening && (
                      <div className={`screening-info ${expandedCards[card.id] ? "expanded" : ""}`}>
                        <button className="screening-info-toggle" onClick={() => toggleCardExpand(card.id)}>
                          📋 {t("results.screeningGuidelines")} {expandedCards[card.id] ? "▲" : "▼"}
                        </button>
                        {expandedCards[card.id] && (
                          <div className="screening-info-content">
                            <div className="screening-detail"><strong>{t("results.method")}:</strong> {card.screening.method}</div>
                            <div className="screening-detail"><strong>{t("results.guideline")}:</strong> {card.screening.guideline}</div>
                            <div className="screening-detail"><strong>{t("results.whoShouldScreen")}:</strong> {card.screening.who}</div>
                            <div className="screening-detail"><strong>{t("results.interval")}:</strong> {card.screening.interval}</div>
                            {card.screening.highRisk && (
                              <div className="screening-detail high-risk-note"><strong>{t("results.highRiskNote")}:</strong> {card.screening.highRisk}</div>
                            )}
                            <div className="screening-source">{t("results.source")}: {card.screening.source}</div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="card-actions">
                      <button className="action-button primary" onClick={handlePrimaryAction}>{card.primaryAction}</button>
                      <button className="action-button secondary" onClick={handleLearnMore}>{card.secondaryAction}</button>
                    </div>
                  </div>
                ))}

                {/* Below-average risk items (hidden by default) */}
                {showBelowAverage && belowAvgCards.map((card) => (
                  <div key={card.id} className={`screening-card ${card.cssClass}`}>
                    <div className="card-header">
                      <div className="card-title"><h2>🩺 {card.title}</h2></div>
                      <div className="risk-level" style={{ backgroundColor: card.riskColor }}>{card.riskLevel}</div>
                    </div>
                    <p className="card-description">{card.description}</p>
                    {card.guidelineNote && (
                      <div className="guideline-note">ℹ️ {card.guidelineNote}</div>
                    )}
                    {card.riskFactors.length > 0 && (
                      <details className="risk-factors-collapsible">
                        <summary className="risk-factors-toggle">
                          {t("results.riskFactors", "Your Risk Factors")} ({card.riskFactors.length}) ▼
                        </summary>
                        <div className="risk-factors">
                          <ul>{card.riskFactors.map((rf, index) => (
                            <li key={index} className="risk-factor-item">
                              <div className="risk-factor-header">
                                <span className="risk-factor-name">{rf.factor || rf}</span>
                                {rf.multiplier && <span className="risk-factor-multiplier">{rf.multiplier.toFixed(1)}x</span>}
                              </div>
                              {rf.detail && <p className="risk-factor-detail">{rf.detail}</p>}
                            </li>
                          ))}</ul>
                        </div>
                      </details>
                    )}
                    {card.riskScore > 0 && <RiskMeter score={card.riskScore} level={card.riskLevel} color={card.riskColor} />}
                    <div className="card-actions">
                      <button className="action-button secondary" onClick={handleLearnMore}>{card.secondaryAction}</button>
                    </div>
                  </div>
                ))}

                {/* Show more / Show less toggle */}
                {belowAvgCards.length > 0 && (
                  <button
                    className="show-more-results-btn"
                    onClick={() => setShowBelowAverage(!showBelowAverage)}
                  >
                    {showBelowAverage
                      ? t("results.showLess", "Show less ▲")
                      : t("results.showMore", `Show ${belowAvgCards.length} more result${belowAvgCards.length > 1 ? 's' : ''} ▼`)}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Fallback: original layout when feature flag is off */}
          {!FEATURE_FLAGS.PRIMARY_SECONDARY_DASHBOARD && <div className="screening-cards">
            {screeningCards.map((card) => (
              <div
                key={card.id}
                className={`screening-card ${card.cssClass}`}
              >
                <div className="card-header">
                  <div className="card-title">
                    <h2>🩺 {card.title}</h2>
                  </div>
                  <div
                    className="risk-level"
                    style={{ backgroundColor: card.riskColor }}
                  >
                    {card.riskLevel}
                  </div>
                </div>

                {/* Risk Meter */}
                {card.riskScore > 0 && (
                  <RiskMeter
                    score={card.riskScore}
                    level={card.riskLevel}
                    color={card.riskColor}
                  />
                )}

                <p className="card-description">{card.description}</p>

                {/* Risk Factors with evidence */}
                {card.riskFactors.length > 0 && (
                  <div className="risk-factors">
                    <h3>{t("results.riskFactors")}:</h3>
                    <ul>
                      {card.riskFactors.map((rf, index) => (
                        <li key={index} className="risk-factor-item">
                          <div className="risk-factor-header">
                            <span className="risk-factor-name">
                              {rf.factor || rf}
                            </span>
                            {rf.multiplier && (
                              <span className="risk-factor-multiplier">
                                {rf.multiplier.toFixed(1)}x
                              </span>
                            )}
                          </div>
                          {rf.detail && (
                            <p className="risk-factor-detail">{rf.detail}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Statistics */}
                {card.statistics && card.statistics.length > 0 && (
                  <div className="statistics-section">
                    <h3>{t("results.statistics")}:</h3>
                    <div className="statistics-grid">
                      {card.statistics.map((stat, index) => (
                        <div key={index} className="stat-item">
                          <span className="stat-label">{stat.label}</span>
                          <span className="stat-value">{stat.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Screening recommendation */}
                {card.screening && (
                  <div
                    className={`screening-info ${
                      expandedCards[card.id] ? "expanded" : ""
                    }`}
                  >
                    <button
                      className="screening-info-toggle"
                      onClick={() => toggleCardExpand(card.id)}
                    >
                      📋 {t("results.screeningGuidelines")}{" "}
                      {expandedCards[card.id] ? "▲" : "▼"}
                    </button>
                    {expandedCards[card.id] && (
                      <div className="screening-info-content">
                        <div className="screening-detail">
                          <strong>{t("results.method")}:</strong>{" "}
                          {card.screening.method}
                        </div>
                        <div className="screening-detail">
                          <strong>{t("results.guideline")}:</strong>{" "}
                          {card.screening.guideline}
                        </div>
                        <div className="screening-detail">
                          <strong>{t("results.whoShouldScreen")}:</strong>{" "}
                          {card.screening.who}
                        </div>
                        <div className="screening-detail">
                          <strong>{t("results.interval")}:</strong>{" "}
                          {card.screening.interval}
                        </div>
                        {card.screening.highRisk && (
                          <div className="screening-detail high-risk-note">
                            <strong>{t("results.highRiskNote")}:</strong>{" "}
                            {card.screening.highRisk}
                          </div>
                        )}
                        <div className="screening-source">
                          {t("results.source")}: {card.screening.source}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="card-actions">
                  <button
                    className="action-button primary"
                    onClick={handlePrimaryAction}
                  >
                    {card.primaryAction}
                  </button>
                  <button
                    className="action-button secondary"
                    onClick={handleLearnMore}
                  >
                    {card.secondaryAction}
                  </button>
                </div>
              </div>
            ))}
          </div>}

          {/* Sources Section - Collapsible */}
          <details className="sources-section">
            <summary className="sources-toggle"><h2>{t("results.sourcesTitle")}</h2></summary>
            <ul>
              <li>
                U.S. Preventive Services Task Force (USPSTF) — A/B Grade
                Screening Recommendations (2018-2024)
              </li>
              <li>
                American Cancer Society (ACS) — Cancer Screening Guidelines
                (2024-2025)
              </li>
              <li>
                National Cancer Institute (NCI) — SEER Cancer Statistics Review
              </li>
              <li>
                World Cancer Research Fund / AICR — Diet, Nutrition, Physical
                Activity and Cancer: a Global Perspective (3rd Expert Report)
              </li>
              <li>
                International Agency for Research on Cancer (IARC/WHO) —
                Monographs on Carcinogenicity
              </li>
              <li>
                American Association for the Study of Liver Diseases (AASLD) —
                HCC Surveillance Guidelines (2023)
              </li>
            </ul>
            <h3 className="academic-references-title">{t("results.recentResearch")}</h3>
            <ol className="academic-references-list">
              {ACADEMIC_REFERENCES.map((ref) => (
                <li key={ref.id} className="academic-reference-item">
                  <span className="ref-authors">{ref.authors}</span>{" "}
                  <span className="ref-title">"{ref.title}."</span>{" "}
                  <em className="ref-journal">{ref.journal}</em> ({ref.year}); {ref.volume}.
                  {ref.pmid && (
                    <a
                      href={`https://pubmed.ncbi.nlm.nih.gov/${ref.pmid}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ref-pmid"
                    >
                      {" "}PMID: {ref.pmid}
                    </a>
                  )}
                  <p className="ref-finding">↳ {ref.finding}</p>
                </li>
              ))}
            </ol>
            <p className="sources-disclaimer">{t("results.disclaimer")}</p>
          </details>
        </div>

        {/* Health Screening — Blood Sugar, Cholesterol, Blood Pressure */}
        {FEATURE_FLAGS.HEALTH_SCREENING_OVERVIEW && !FEATURE_FLAGS.IMPROVED_VITALS_FLOW && healthScreening.length > 0 && (
          <div className="health-screening-section">
            <h2 className="health-screening-title">🏥 {t("results.healthScreeningTitle", "Your Health Screening Overview")}</h2>
            <p className="health-screening-subtitle">{t("results.healthScreeningSubtitle", "Based on your responses, here are personalized recommendations for key health metrics.")}</p>
            <div className="health-screening-cards">
              {healthScreening.map((rec) => {
                const isExpanded = expandedCards[rec.id];
                const riskColorMap = {
                  diagnosed: "#dc3545",
                  high: "#fd7e14",
                  moderate: "#ffc107",
                  low: "#28a745",
                };
                const riskLabelMap = {
                  diagnosed: t("results.riskDiagnosed", "Diagnosed"),
                  high: t("results.riskHigh", "High Risk"),
                  moderate: t("results.riskModerate", "Moderate Risk"),
                  low: t("results.riskLow", "Low Risk"),
                };
                return (
                  <div
                    key={rec.id}
                    className={`health-screening-card risk-${rec.riskLevel}`}
                    onClick={() => toggleCardExpand(rec.id)}
                  >
                    <div className="hs-card-header">
                      <span className="hs-icon">{rec.icon}</span>
                      <div className="hs-card-title-group">
                        <h3 className="hs-card-title">{rec.category}</h3>
                        <span
                          className="hs-risk-badge"
                          style={{ backgroundColor: riskColorMap[rec.riskLevel] || "#28a745" }}
                        >
                          {riskLabelMap[rec.riskLevel] || rec.riskLevel}
                        </span>
                      </div>
                    </div>
                    {rec.riskFactors.length > 0 && (
                      <div className="hs-risk-factors">
                        {rec.riskFactors.map((rf, i) => (
                          <span key={i} className="hs-risk-factor-tag">• {rf}</span>
                        ))}
                      </div>
                    )}
                    <div className={`hs-card-details ${isExpanded ? "expanded" : ""}`}>
                      <div className="hs-screening-info">
                        <h4>{t("results.screeningMethod", "Screening Method")}</h4>
                        <p>{rec.screening.method}</p>
                        <h4>{t("results.recommendedInterval", "Recommended Interval")}</h4>
                        <p>{rec.screening.interval}</p>
                        <p className="hs-guideline-source">{rec.screening.guideline} — {rec.screening.source}</p>
                      </div>
                      <div className="hs-action-items">
                        <h4>{t("results.actionItems", "Action Items")}</h4>
                        <ul>
                          {rec.actionItems.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <span className="hs-expand-hint">{isExpanded ? "▲" : "▼"} {isExpanded ? t("results.showLess", "Show less") : t("results.showMore", "Show more")}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Health Manual — Full Width Below Screening */}
        <div className="health-manual-v2">
          <div className="manual-v2-header">
            <h2 className="manual-v2-title">📋 {t("results.healthManual")}</h2>
            <p className="manual-v2-subtitle">
              {t("results.manualSubtitle")}
            </p>
          </div>

          {isStructuredManual ? (
            <div className="manual-v2-sections">
              {healthManualSections.map((section, sIndex) => (
                <div key={sIndex} className="manual-v2-section">
                  <h3 className="manual-v2-section-title">
                    <span className="section-icon">{section.icon}</span>
                    {section.title}
                    <span className="section-count">{section.items.length}</span>
                  </h3>
                  <div className="manual-v2-cards">
                    {section.items.map((item, iIndex) => (
                      <div
                        key={iIndex}
                        className={`manual-v2-card ${getPriorityClass(item.priority)}`}
                      >
                        <div className="manual-v2-card-header">
                          <span className="manual-card-icon">{item.icon}</span>
                          <h4 className="manual-card-title">{item.title}</h4>
                          <span className={`manual-priority-badge ${getPriorityClass(item.priority)}`}>
                            {getPriorityLabel(item.priority)}
                          </span>
                        </div>
                        <p className="manual-card-detail">{item.detail}</p>
                        <div className="manual-card-evidence">
                          <span className="evidence-icon">📄</span>
                          <span className="evidence-text">{item.evidence}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Fallback for v1 string-based manual */
            <div className="manual-v2-sections">
              {healthManualSections.map((section, index) => (
                <div key={index} className="manual-v2-section">
                  <h3 className="manual-v2-section-title">{section.title}</h3>
                  <ul className="manual-v1-list">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{typeof item === "string" ? translateHealthTip(item) : item.title}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className="manual-v2-footer">
            <button
              className="view-manual-button-v2"
              onClick={() => navigate("/health-tips")}
            >
              📚 {t("results.viewFullGuide")}
            </button>
          </div>
        </div>
      </div>

      {/* Share with Your Doctor — PDF Report + Email */}
      <div className="doctor-report-section">
        <div className="doctor-report-card">
          <div className="doctor-report-icon">📋</div>
          <h3 className="doctor-report-title">
            {t("results.reportTitle", "Share This Report with Your Doctor")}
          </h3>
          <p className="doctor-report-subtitle">
            {t("results.reportSubtitle", "Download a professional PDF summary of your screening recommendations to bring to your next appointment.")}
          </p>

          {reportStatus === "emailed" || reportStatus === "downloaded" ? (
            <div className="doctor-report-success">
              <p>✓ {t("results.reportDownloaded", "Your report has been downloaded!")}</p>
              <button className="doctor-report-btn-secondary" onClick={handleDownloadReport}>
                {t("results.reportDownloadAgain", "Download Again")}
              </button>
            </div>
          ) : (
            <div className="doctor-report-actions">
              <button className="doctor-report-btn" onClick={handleDownloadReport}>
                {t("results.reportDownload", "Download PDF Report")}
              </button>
              {typeof navigator !== "undefined" && navigator.share && (
                <button className="doctor-report-btn-secondary" onClick={handleShareReport} style={{ marginTop: 8 }}>
                  📤 {t("results.reportShare", "Share Report")}
                </button>
              )}
              <div className="doctor-report-divider">
                <span>{t("results.reportOr", "or")}</span>
              </div>
              <form className="doctor-report-email-form" onSubmit={handleEmailReport}>
                <p className="doctor-report-email-label">
                  {t("results.reportEmailLabel", "Enter your email to receive this report and future screening reminders:")}
                </p>
                <div className="doctor-report-email-row">
                  <input
                    ref={reportEmailRef}
                    type="email"
                    className="doctor-report-email-input"
                    placeholder={t("results.reportEmailPlaceholder", "your@email.com")}
                    value={reportEmail}
                    onChange={(e) => setReportEmail(e.target.value)}
                    disabled={reportStatus === "sending"}
                  />
                  <button type="submit" className="doctor-report-btn" disabled={reportStatus === "sending"}>
                    {reportStatus === "sending" ? "..." : t("results.reportEmailSend", "Send & Download")}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      <p className="result-disclaimer result-disclaimer-bottom">
        {t("results.disclaimer")}
      </p>
    </div>
  );
};

export default ResultPage;
